// AGC: Generic field renderer — fallback for fields without a specific renderer
// AGC: Two modes: missing field (red card + text/voice input) or non-missing (collapsible suggestions)
import React, { useState } from 'react'
import { Mic, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'
import { useTranslation } from '../../../hooks/useTranslation'
import type { VoiceFieldProps } from './types'

// AGC: Extra prop for suggestion selection callback (non-missing mode)
interface GenericFieldProps extends VoiceFieldProps {
  onFieldSuggestionSelect?: (fieldName: string, selectedValue: string, label: string) => void
}

export const GenericField: React.FC<GenericFieldProps> = ({
  fieldName,
  info,
  inputValues,
  setInputValues,
  wizardMode,
  onFieldSuggestionSelect,
}) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  // AGC: Sizing classes — bigger in wizard mode to fill the full-screen card layout
  const sz = wizardMode
    ? { card: 'p-5', label: 'text-base', input: 'px-4 py-3 text-base', btn: 'px-4 py-3.5 text-base' }
    : { card: 'p-2', label: 'text-xs', input: 'px-2 py-1.5 text-sm', btn: 'px-2 py-1.5 text-xs' }

  // AGC: Missing field mode — red-themed card with text input + voice input
  if (info.missing) {
    return (
      <div className={`${sz.card} bg-red-500/10 border border-red-500/30 rounded-lg`}>
        <div className="flex items-center justify-between">
          <span className={`text-red-300 ${sz.label} font-medium`}>
            {info.fieldLabel}
          </span>
        </div>
        {info.reason && (
          <p className="text-white/40 text-xs mt-0.5">{info.reason}</p>
        )}

        {/* AGC: Text input with optional voice button (no per-field checkmark) */}
        {info.allowTextInput && (
          <div className="flex items-center gap-2 mt-2">
            <input
              type={info.inputType || 'text'}
              placeholder={info.inputType === 'number' ? '0' : t('voice.typeHere')}
              value={inputValues[fieldName] || ''}
              onChange={(e) => setInputValues(prev => ({ ...prev, [fieldName]: e.target.value }))}
              className={`flex-1 ${sz.input} bg-white/10 border border-white/20 rounded text-white placeholder-white/30 focus:outline-none focus:border-emerald-500`}
            />
            {/* AGC: Only voice input button, no checkmark */}
            {info.allowVoiceInput && (
              <button
                onClick={() => {
                  // AGC: Use Web Speech API for quick voice input
                  if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
                    const SpeechRecognitionAPI = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition
                    const recognition = new SpeechRecognitionAPI()
                    recognition.lang = 'pt-BR'
                    recognition.continuous = false
                    recognition.interimResults = false

                    toast.loading('🎤 ' + t('voice.speakValue'), { id: 'voice-field-input' })

                    recognition.onresult = (event: any) => {
                      const transcript = event.results[0][0].transcript
                      // AGC: Extract numbers from transcript
                      const numbers = transcript.match(/\d+/g)
                      if (numbers && numbers.length > 0) {
                        const value = numbers.join('')
                        setInputValues(prev => ({ ...prev, [fieldName]: value }))
                        toast.success(`✅ ${t('voice.valueCaptured')}: ${value}`, { id: 'voice-field-input' })
                      } else {
                        // AGC: Try to convert written numbers
                        const writtenNumbers: Record<string, string> = {
                          'zero': '0', 'um': '1', 'uma': '1', 'dois': '2', 'duas': '2',
                          'três': '3', 'tres': '3', 'quatro': '4', 'cinco': '5',
                          'seis': '6', 'sete': '7', 'oito': '8', 'nove': '9', 'dez': '10',
                          'vinte': '20', 'trinta': '30', 'quarenta': '40', 'cinquenta': '50',
                          'cem': '100', 'duzentos': '200', 'trezentos': '300',
                          'mil': '1000',
                        }
                        const words = transcript.toLowerCase().split(/\s+/)
                        let foundNumber = ''
                        for (const word of words) {
                          if (writtenNumbers[word]) {
                            foundNumber = writtenNumbers[word]
                            break
                          }
                        }
                        if (foundNumber) {
                          setInputValues(prev => ({ ...prev, [fieldName]: foundNumber }))
                          toast.success(`✅ ${t('voice.valueCaptured')}: ${foundNumber}`, { id: 'voice-field-input' })
                        } else {
                          setInputValues(prev => ({ ...prev, [fieldName]: transcript }))
                          toast.success(`✅ "${transcript}"`, { id: 'voice-field-input' })
                        }
                      }
                    }

                    recognition.onerror = () => {
                      toast.error(t('voice.captureError'), { id: 'voice-field-input' })
                    }

                    recognition.start()
                  } else {
                    toast.error(t('voice.notSupported'))
                  }
                }}
                className="p-1.5 bg-blue-500/20 hover:bg-blue-500/30 rounded text-blue-300"
                title={t('voice.voiceInput')}
              >
                <Mic className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    )
  }

  // AGC: Non-missing field mode — collapsible button with original value + suggestions dropdown
  // AGC: Chevron icon size scales with wizard mode
  const chevronSize = wizardMode ? 'w-6 h-6' : 'w-4 h-4'

  return (
    <>
      {/* AGC: AI translated info */}
      {info.aiTranslated && info.translatedValue && (
        <p className={`text-blue-300 ${wizardMode ? 'text-sm' : 'text-xs'} mb-1`}>
          🌍 &quot;{info.originalValue}&quot; = &quot;{info.translatedValue}&quot;
        </p>
      )}

      <button
        onClick={() => setIsExpanded(prev => !prev)}
        className={`w-full flex items-center justify-between text-left ${sz.card} bg-white/5 hover:bg-white/10 rounded-xl transition-colors`}
      >
        <div className="flex-1 min-w-0">
          <span className={`text-white/60 ${sz.label}`}>{info.fieldLabel}:</span>
          <span className={`text-yellow-300 ${wizardMode ? 'text-lg' : 'text-sm'} ml-2 truncate`}>&quot;{info.originalValue}&quot;</span>
        </div>
        <ChevronRight
          className={`${chevronSize} text-white/60 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
        />
      </button>

      {/* AGC: Suggestions dropdown */}
      {isExpanded && (
        <div className={`mt-2 ${wizardMode ? 'space-y-2' : 'space-y-1'} animate-fade-in`}>
          {(info.suggestions || []).slice(0, 3).map((suggestion: { value: string; label: string; confidence: number }, idx: number) => (
            <button
              key={suggestion.value}
              onClick={() => {
                onFieldSuggestionSelect?.(fieldName, suggestion.value, suggestion.label)
                setIsExpanded(false)
              }}
              className={`w-full flex items-center justify-between ${sz.btn} rounded-lg text-left transition-colors ${
                idx === 0
                  ? 'bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40'
                  : 'bg-white/5 hover:bg-white/10'
              }`}
            >
              <span className={`${sz.label} ${idx === 0 ? 'text-emerald-300' : 'text-white/80'}`}>
                {suggestion.label}
              </span>
              <span className={`${wizardMode ? 'text-sm' : 'text-xs'} text-white/40`}>
                {suggestion.confidence}%
              </span>
            </button>
          ))}

          {/* AGC: Custom text input option */}
          {info.allowTextInput && (
            <div className="flex items-center gap-2 mt-2">
              <input
                type="text"
                placeholder={t('voice.otherValue')}
                value={inputValues[fieldName] || ''}
                onChange={(e) => setInputValues(prev => ({ ...prev, [fieldName]: e.target.value }))}
                className={`flex-1 ${sz.input} bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-emerald-500`}
              />
            </div>
          )}
        </div>
      )}
    </>
  )
}
